import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import HelpCircleOutlineIcon from 'mdi-react/HelpCircleOutlineIcon';
import { bool, func, object, string } from 'prop-types';
import React, { useState } from 'react';
import ToolPanelSection from 'components/@home/@settings/Tabs/Agents/toolPanels/ToolPanelSection';
import { TOOL_TRIGGERS } from 'constants/agenticToolTriggers';
import { I18n } from 'utils/i18n';

const TRIGGERS = TOOL_TRIGGERS;

const SwitchRow = ({
  label,
  helpTip = null,
  description = null,
  checked,
  onChange,
  disabled = false,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      py: 0.5,
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, minWidth: 0, flex: 1 }}>
      <Typography
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: theme => theme.typography.pxToRem(13),
          fontWeight: 500,
          color: 'text.primary',
          lineHeight: 1.3,
        }}
      >
        {label}
        {helpTip && (
          <Tooltip title={helpTip} placement="top" arrow>
            <IconButton size="small" sx={{ p: 0, color: 'text.secondary' }} aria-label={label}>
              <HelpCircleOutlineIcon size={14} />
            </IconButton>
          </Tooltip>
        )}
      </Typography>
      {description && (
        <Typography
          component="span"
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            color: 'text.secondary',
            lineHeight: 1.5,
            maxWidth: 700,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      disabled={disabled}
      size="small"
      sx={{ mt: 0.25, flexShrink: 0 }}
    />
  </Box>
);

SwitchRow.propTypes = {
  label: string.isRequired,
  helpTip: string,
  description: string,
  checked: bool.isRequired,
  onChange: func.isRequired,
  disabled: bool,
};

const GeneralPanel = ({ tool, setTool }) => {
  const skipFirstAnnouncement = (tool.announcement?.whenToAnnounce || 'never') === 'never';
  const hasOverride = Boolean(tool.announcement?.calling?.trim());
  const [overrideOpen, setOverrideOpen] = useState(hasOverride);

  const patchAnnouncement = patch => {
    setTool({
      ...tool,
      announcement: { ...(tool.announcement || {}), ...patch },
    });
  };

  const patchLongAction = patch => {
    setTool({
      ...tool,
      longActionNotice: { ...(tool.longActionNotice || {}), ...patch },
    });
  };

  const currentTrigger = tool.trigger || 'agent';

  return (
    <Stack spacing={2} sx={{ maxWidth: 880 }}>
      <ToolPanelSection
        title={I18n.t('AgenticChatFlow.Trigger')}
        description={I18n.t('AgenticChatFlow.TriggerSectionDescription')}
      >
        <Stack spacing={1} sx={{ maxWidth: 480 }}>
          <TextField
            select
            value={currentTrigger}
            onChange={e => setTool({ ...tool, trigger: e.target.value })}
            size="small"
            fullWidth
          >
            {TRIGGERS.map(t => (
              <MenuItem key={t} value={t}>
                {I18n.t(`AgenticChatFlow.Trigger_${t}`)}
              </MenuItem>
            ))}
          </TextField>
          <Typography
            sx={{
              fontSize: theme => theme.typography.pxToRem(12),
              color: 'text.secondary',
              lineHeight: 1.5,
            }}
          >
            {I18n.t(`AgenticChatFlow.TriggerDesc_${currentTrigger}`)}
          </Typography>
        </Stack>
      </ToolPanelSection>

      <ToolPanelSection
        title={I18n.t('AgenticChatFlow.FirstToolAnnouncement')}
        description={I18n.t('AgenticChatFlow.FirstToolAnnouncementDescription')}
      >
        <Stack spacing={2}>
          <SwitchRow
            label={I18n.t('AgenticChatFlow.SkipFirstAnnouncement')}
            helpTip={I18n.t('AgenticChatFlow.SkipFirstAnnouncementHelp')}
            checked={skipFirstAnnouncement}
            onChange={checked => {
              patchAnnouncement({
                whenToAnnounce: checked ? 'never' : 'always',
              });
            }}
          />

          <Box
            sx={[
              {
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                opacity: 1,
                transition: 'opacity 0.15s',
              },
              skipFirstAnnouncement && {
                opacity: 0.55,
                bgcolor: 'action.subtleLight',
              },
            ]}
          >
            <Box
              component="button"
              type="button"
              disabled={skipFirstAnnouncement}
              onClick={() => !skipFirstAnnouncement && setOverrideOpen(v => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                width: '100%',
                p: 1.25,
                px: 1.75,
                border: 'none',
                bgcolor: 'transparent',
                cursor: skipFirstAnnouncement ? 'not-allowed' : 'pointer',
                textAlign: 'left',
                font: 'inherit',
                color: 'text.primary',
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.25,
                  fontSize: theme => theme.typography.pxToRem(13),
                  fontWeight: 500,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    transition: 'transform 0.18s ease',
                    color: 'text.secondary',
                    flexShrink: 0,
                    transform: overrideOpen && !skipFirstAnnouncement ? 'rotate(90deg)' : 'none',
                  }}
                >
                  <ChevronRightIcon size={10} />
                </Box>
                {I18n.t('AgenticChatFlow.AnnouncementOverride')}
                <Box
                  component="span"
                  sx={{
                    fontSize: theme => theme.typography.pxToRem(12),
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  {I18n.t('AgenticChatFlow.Optional')}
                </Box>
              </Box>
            </Box>
            <Collapse in={overrideOpen && !skipFirstAnnouncement}>
              <Box sx={{ px: 1.75, pb: 1.75, borderTop: 1, borderColor: 'divider' }}>
                <Typography
                  sx={{
                    fontSize: theme => theme.typography.pxToRem(12),
                    color: 'text.secondary',
                    lineHeight: 1.5,
                    mb: 1,
                  }}
                >
                  {I18n.t('AgenticChatFlow.AnnouncementOverrideHint')}
                </Typography>
                <TextField
                  value={tool.announcement?.calling || ''}
                  onChange={e => patchAnnouncement({ calling: e.target.value })}
                  size="small"
                  fullWidth
                  placeholder={I18n.t('AgenticChatFlow.AnnouncementOverridePlaceholder')}
                />
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </ToolPanelSection>

      <ToolPanelSection
        title={I18n.t('AgenticChatFlow.Behavior')}
        description={I18n.t('AgenticChatFlow.BehaviorDescription')}
      >
        <Box>
          <SwitchRow
            label={I18n.t('AgenticChatFlow.LongActionNotice')}
            helpTip={I18n.t('AgenticChatFlow.LongActionNoticeHelp')}
            description={I18n.t('AgenticChatFlow.LongActionNoticeDescription')}
            checked={!!tool.longActionNotice?.enabled}
            onChange={checked => patchLongAction({ enabled: checked })}
          />
          {tool.longActionNotice?.enabled && (
            <TextField
              type="number"
              label={I18n.t('AgenticChatFlow.LongActionNoticeWait')}
              value={tool.longActionNotice?.waitSeconds ?? 0}
              onChange={e => patchLongAction({ waitSeconds: Number(e.target.value) || 0 })}
              size="small"
              sx={{ mt: 1.5, maxWidth: 240 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {I18n.t('AgenticChatFlow.Seconds')}
                    </Typography>
                  ),
                },
              }}
            />
          )}

          <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 2 }}>
            <SwitchRow
              label={I18n.t('AgenticChatFlow.ForbidCancellation')}
              description={I18n.t('AgenticChatFlow.ForbidCancellationDescription')}
              checked={!!tool.forbidCancellation}
              onChange={checked => setTool({ ...tool, forbidCancellation: checked })}
            />
          </Box>

          <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 2 }}>
            <SwitchRow
              label={I18n.t('AgenticChatFlow.ForbidUserInterrupts')}
              description={I18n.t('AgenticChatFlow.ForbidUserInterruptsDescription')}
              checked={!!tool.forbidUserInterrupt}
              onChange={checked => setTool({ ...tool, forbidUserInterrupt: checked })}
            />
          </Box>
        </Box>
      </ToolPanelSection>

      {tool.kind === 'code' && (
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            color: 'text.secondary',
            lineHeight: 1.5,
          }}
        >
          {I18n.t('AgenticChatFlow.CodeKeyHelp', { key: tool.codeKey || tool.name })}
        </Typography>
      )}
    </Stack>
  );
};

GeneralPanel.propTypes = {
  tool: object.isRequired,
  setTool: func.isRequired,
};

export default GeneralPanel;
